import { Link } from "react-router-dom";
import { useState } from "react";
import { PawPrint, Instagram, MessageCircle, Mail, MapPin, Phone, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { subscribeNewsletter } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      if (res.status === "already_subscribed") {
        toast.info("You're already on our list — thank you!");
      } else {
        toast.success("Welcome to the pack. Check your inbox soon.");
      }
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative z-10 bg-forest text-bone mt-32 pb-[88px] lg:pb-[80px]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-terracotta flex items-center justify-center">
              <PawPrint className="w-5 h-5 text-bone" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-serif text-2xl">Pawsomeland Foundation</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-bone/60">Cage-Free · Bhopal</div>
            </div>
          </div>
          <p className="font-serif text-2xl leading-snug max-w-md text-bone/90">
            "No animal should have to earn their freedom. We believe every paw deserves open ground."
          </p>
          <form onSubmit={handleSubscribe} className="flex gap-2 max-w-md" data-testid="newsletter-form">
            <Input
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-bone/10 border-bone/20 text-bone placeholder:text-bone/50 rounded-full h-12 px-5"
              data-testid="newsletter-email-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="bg-terracotta hover:bg-terracotta-deep text-bone rounded-full px-6 h-12"
              data-testid="newsletter-submit-button"
            >
              {loading ? "..." : "Subscribe"}
            </Button>
          </form>
        </div>

        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-bone/50 mb-5">Explore</div>
          <ul className="space-y-3 text-bone/85">
            <li><Link to="/about" className="link-underline" data-testid="footer-about-link">Our Story</Link></li>
            <li><Link to="/adopt" className="link-underline" data-testid="footer-adopt-link">Adopt</Link></li>
            <li><Link to="/adopt" className="link-underline" data-testid="footer-volunteer-link">Volunteer</Link></li>
            <li><Link to="/stories" className="link-underline" data-testid="footer-stories-link">Stories</Link></li>
            <li><Link to="/donate" className="link-underline" data-testid="footer-donate-link">Donate</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-3 space-y-5">
          <div className="text-xs uppercase tracking-[0.2em] text-bone/50">Visit our shelter</div>
          <div className="flex items-start gap-3 text-bone/85" data-testid="footer-shelter-address">
            <MapPin className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
            <p>Waterfall Road<br/>Near Gaushala Barkhedi Obdullah<br/>Bhadbhada Ghat, Bhopal</p>
          </div>
          <div className="flex items-start gap-3 text-bone/85" data-testid="footer-office-address">
            <Building2 className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
            <p><span className="text-[10px] uppercase tracking-[0.2em] text-bone/55 block mb-0.5">Office</span>House no. 20, Kaveri Tailors<br/>Ganesh Mandir, Chhola Road<br/>Bhopal</p>
          </div>
          <div className="flex items-start gap-3 text-bone/85" data-testid="footer-phone">
            <Phone className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
            <a href="tel:+917477216753" className="link-underline">+91 74772 16753</a>
          </div>
          <div className="flex items-start gap-3 text-bone/85">
            <Mail className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
            <a href="mailto:pawsomelandfoundation@gmail.com" className="link-underline" data-testid="footer-email-link">
              pawsomelandfoundation@gmail.com
            </a>
          </div>
          <div className="flex gap-3 pt-2">
            <a href="https://www.instagram.com/the.pawsome.land/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-bone/20 hover:bg-terracotta hover:border-terracotta flex items-center justify-center transition-all" data-testid="social-instagram-link"><Instagram className="w-4 h-4" strokeWidth={1.5}/></a>
            <a href="https://wa.me/917477216753?text=Hi%20Pawsomeland%20Foundation%2C%20I%27d%20love%20to%20know%20more." target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 rounded-full border border-bone/20 hover:bg-terracotta hover:border-terracotta flex items-center justify-center transition-all" data-testid="social-whatsapp-link"><MessageCircle className="w-4 h-4" strokeWidth={1.5}/></a>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4" data-testid="footer-qr-block">
          <div className="text-xs uppercase tracking-[0.2em] text-bone/50">Donate via UPI</div>
          <div className="bg-bone rounded-2xl p-4 max-w-[220px]">
            <img src="/qr-upi.jpg" alt="Pawsomeland Foundation UPI QR code" className="w-full h-auto rounded-lg" />
          </div>
          <div className="text-xs text-bone/70 leading-relaxed max-w-[240px] space-y-1.5">
            <div>
              UPI Number:&nbsp;
              <a href="upi://pay?pa=7477216753@upi&pn=Pawsomeland%20Foundation" className="font-mono text-bone select-all hover:text-honey transition-colors" data-testid="footer-upi-number">7477216753</a>
            </div>
            <div>
              VPA: <span className="font-mono text-bone select-all">merchant1613559.augp@aubank</span>
            </div>
            <div className="text-bone/50">Scan with any UPI app (GPay / PhonePe / Paytm / BHIM)</div>
          </div>
        </div>
      </div>

      <div className="border-t border-bone/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-5 flex flex-col lg:flex-row items-center lg:justify-between gap-3 text-xs text-bone/55" data-testid="footer-legal-links">
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/terms" className="hover:text-bone transition-colors" data-testid="footer-link-terms">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-bone transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link>
            <Link to="/refund-policy" className="hover:text-bone transition-colors" data-testid="footer-link-refund">Cancellation &amp; Refund</Link>
            <Link to="/shipping-policy" className="hover:text-bone transition-colors" data-testid="footer-link-shipping">Shipping &amp; Exchange</Link>
          </nav>
        </div>
      </div>
      <div className="border-t border-bone/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-bone/55">
          <p data-testid="footer-cin">© {new Date().getFullYear()} Pawsomeland Foundation. Registered 80G &amp; 12A · CIN: <span className="font-mono text-bone/75">U88900MP2024NPL070251</span></p>
          <p>Built with paws, in India.</p>
        </div>
      </div>
    </footer>
  );
}
